'use es6';

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

let _ = t => t,
    _t,
    _t2,
    _t3;

import MIDDLE_BLOB_IMAGE from 'bender-url!pricing-pages-unified-ui/img/middle-blob.svg';
import { CALYPSO_DARK } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { CRM_SUITE_PRODUCTS, highestProductLevel } from 'pricing-pages-lib/utils/productsUtils';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import H1 from 'UIComponents/elements/headings/H1';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UILink from 'UIComponents/link/UILink';
import UITile from 'UIComponents/tile/UITile';
import UITileSection from 'UIComponents/tile/UITileSection';
import { tabIdToProductLevels } from '../../constants/Mon569SidenavConfig';
import { CALCULATOR, SCREENS, SUITE_CALCULATOR } from '../../constants/Tracking';
import { isMon569VariantSelector } from '../../selectors/ExperimentTreatmentSelectors';
import { hasPricingAndPurchasingSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { suiteProductConfigurationsSelector } from '../../selectors/ProductConfigsSelectors';
import { highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { currentSkuIdForProductSelector, lowestPaidProductLevelFromListSelector, lowestPaidProductLevelSelector, skuSelector } from '../../selectors/ProductsSelectors';
import classNames from '../../utils/classNames';
import breakpoint from '../../utils/responsiveBreakpoints';
import AddonsAndServicesPickerList from '../plan-builder/AddonsAndServicesPickerList';
import HubPlanPickerList from '../plan-builder/HubPlanPickerList';
import PlanBuilder from '../plan-builder/PlanBuilder';
import SuitePlanPicker from '../plan-builder/SuitePlanPicker';
import SummaryColumn from '../plan-builder/SummaryColumn';
const SummaryColumnWrapper = styled.div.withConfig({
  displayName: "Calculator__SummaryColumnWrapper",
  componentId: "sc-1wys5a-0"
})(["width:360px;position:sticky;", ""], breakpoint.tablet(_t || (_t = _`
    width: 100%;
  `)));
const RotatedIcon = styled(UIIcon).withConfig({
  displayName: "Calculator__RotatedIcon",
  componentId: "sc-1wys5a-1"
})(["transform:rotate(-90deg);"]);
const ResponsiveTileSection = styled(UITileSection).withConfig({
  displayName: "Calculator__ResponsiveTileSection",
  componentId: "sc-1wys5a-2"
})(["", ""], breakpoint.phone(_t2 || (_t2 = _`
    padding: 20px;
  `)));
const StyledImageBackground = styled(UIImage).withConfig({
  displayName: "Calculator__StyledImageBackground",
  componentId: "sc-1wys5a-3"
})(["position:absolute;top:0;width:100%;max-height:800px;max-height:min(800px,100%);"]); // IE doesn't support min()

const ContentContainer = styled.div.withConfig({
  displayName: "Calculator__ContentContainer",
  componentId: "sc-1wys5a-4"
})(["", ""], breakpoint.tablet(_t3 || (_t3 = _`
    width: 100%;
  `)));

const BackToTopLink = () => /*#__PURE__*/_jsx(UIFlex, {
  justify: "end",
  children: /*#__PURE__*/_jsxs(UILink, {
    className: "m-bottom-2",
    onClick: () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      getUsageTracker().track('scrollInteraction', {
        name: 'calculator',
        action: 'clicked back to top'
      });
    },
    children: [/*#__PURE__*/_jsx(RotatedIcon, {
      name: "next",
      size: "small",
      color: CALYPSO_DARK
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "pricingPage.planBuilder.productPages.backToTop"
    })]
  })
});

const Calculator = ({
  planBuilderProps,
  planSelectorComponent,
  className,
  hideCrmSuiteAlert = false
}) => {
  const elementId = 'calculator';
  const isMon569Variant = useSelector(isMon569VariantSelector);
  const foundConfig = planBuilderProps.initialProductConfigurations.find(config => config && config.skuId);
  const key = foundConfig ? foundConfig.skuId : 'initial';
  return /*#__PURE__*/_jsxs("div", {
    id: elementId,
    className: classNames(className, 'p-top-10'),
    style: {
      position: 'relative',
      flexDirection: 'column'
    },
    children: [/*#__PURE__*/_jsx(StyledImageBackground, {
      src: MIDDLE_BLOB_IMAGE
    }), /*#__PURE__*/_jsxs("div", {
      style: {
        position: 'relative',
        zIndex: 1
      },
      children: [/*#__PURE__*/_jsx(H1, {
        className: "text-center m-top-10 m-bottom-0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "pricingPage.planBuilder.productPages.title"
        })
      }), /*#__PURE__*/_jsxs(PlanBuilder, Object.assign({
        headerContent: /*#__PURE__*/_jsx(BackToTopLink, {})
      }, planBuilderProps, {
        children: [/*#__PURE__*/_jsx(ContentContainer, {
          className: "flex-column",
          style: {
            flex: 1
          },
          children: /*#__PURE__*/_jsx(UITile, {
            children: /*#__PURE__*/_jsx(ResponsiveTileSection, {
              children: planSelectorComponent
            })
          })
        }), /*#__PURE__*/_jsx(SummaryColumnWrapper, {
          className: "flex-column",
          isMon569Variant: isMon569Variant,
          children: /*#__PURE__*/_jsx(SummaryColumn, {
            shareLinkAnchor: elementId,
            hideCrmSuiteAlert: hideCrmSuiteAlert
          })
        })]
      }), key)]
    })]
  });
};

export const HubCalculator = ({
  productLine,
  screen,
  className = ''
}) => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const {
    tab
  } = useParams();
  const initialSkuId = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return null;
    const isMon569Variant = isMon569VariantSelector(state);
    let lowestPaidProductLevel;

    if (isMon569Variant) {
      lowestPaidProductLevel = lowestPaidProductLevelFromListSelector(state, {
        productLine,
        productLevels: tabIdToProductLevels[tab]
      });
    } else {
      lowestPaidProductLevel = lowestPaidProductLevelSelector(state, {
        productLine
      });
    }

    const productLevel = highestProductLevel(lowestPaidProductLevel, highestProductLevelFromProductInventorySelector(state, {
      productLine
    }));
    return currentSkuIdForProductSelector(state, {
      productLine,
      productLevel
    });
  });
  const zeroStateProductConfigs = useSelector(state => {
    if (!initialSkuId) return [];
    const {
      quantityPackDetails
    } = skuSelector(state, {
      skuId: initialSkuId
    });
    return quantityPackDetails.map(({
      skuId
    }) => createProductConfig({
      skuId,
      quantity: 0
    }));
  });
  return /*#__PURE__*/_jsx(Calculator, {
    className: className,
    planBuilderProps: {
      initialProductConfigurations: [createProductConfig({
        skuId: initialSkuId
      })],
      requiredProductLines: [productLine],
      zeroStateCheckoutProductConfigs: zeroStateProductConfigs,
      trackingSource: CALCULATOR,
      screen,
      showDiscountedPricesInUnitsPicker: true
    },
    trackingSource: CALCULATOR,
    planSelectorComponent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HubPlanPickerList, {
        productLines: [productLine],
        useCompactTitle: true
      }), /*#__PURE__*/_jsx(AddonsAndServicesPickerList, {
        compact: true,
        defaultOpen: false,
        showServicesSection: false,
        screen: screen
      })]
    })
  });
};
export const SuiteCalculator = ({
  className,
  initialProductLevel
}) => {
  const initialProductConfigurations = useSelector(state => suiteProductConfigurationsSelector(state, {
    productLevel: initialProductLevel
  }));
  const allCrmSuiteProductLines = Array.from(Object.values(CRM_SUITE_PRODUCTS).reduce((acc, productLines) => {
    productLines.forEach(productLine => acc.add(productLine));
    return acc;
  }, new Set()));
  const zeroStateProductConfigs = useSelector(state => {
    const skuIds = initialProductConfigurations.map(config => config.skuId);
    return skuIds.reduce((acc, initialSkuId) => {
      const {
        quantityPackDetails
      } = skuSelector(state, {
        skuId: initialSkuId
      });
      const quantityPackConfigs = quantityPackDetails.map(({
        skuId
      }) => createProductConfig({
        skuId,
        quantity: 0
      })) || [];
      return [...acc, ...quantityPackConfigs];
    }, []);
  });
  return /*#__PURE__*/_jsx(Calculator, {
    planBuilderProps: {
      initialProductConfigurations,
      requiredProductLines: allCrmSuiteProductLines,
      trackingSource: SUITE_CALCULATOR,
      screen: SCREENS.GROWTH_SUITE,
      showDiscountedPricesInUnitsPicker: true,
      zeroStateCheckoutProductConfigs: zeroStateProductConfigs
    },
    className: className,
    hideCrmSuiteAlert: true,
    planSelectorComponent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(SuitePlanPicker, {}), /*#__PURE__*/_jsx(AddonsAndServicesPickerList, {
        compact: true,
        defaultOpen: false,
        showServicesSection: false,
        screen: SCREENS.GROWTH_SUITE
      })]
    })
  });
};